import { useEffect, useState } from 'react';
import { Utensils } from 'lucide-react';

import { resolveRestaurantProductImage } from '../restaurantProductImage';

interface Props {
  /** `effectiveImagePath` del ítem del menú (imagen propia o la del producto). */
  path?: string | null;
  alt: string;
  /** Clases para el contenedor (tamaño / posición). */
  className?: string;
}

type State = 'loading' | 'ready' | 'fallback';

/**
 * Muestra la foto del producto en las tarjetas del menú de la orden.
 * Si el ítem no tiene imagen (o falla la descarga) cae a un ícono de cubiertos
 * sobre fondo neutro, la imagen por defecto del módulo de restaurante.
 */
const RestaurantMenuItemImage = ({ path, alt, className = '' }: Props) => {
  const [url, setUrl] = useState<string | null>(null);
  const [state, setState] = useState<State>('loading');

  useEffect(() => {
    let cancelled = false;

    setUrl(null);
    setState(path && path.trim() ? 'loading' : 'fallback');

    if (!path || !path.trim()) return;

    resolveRestaurantProductImage(path)
      .then((resolved) => {
        if (cancelled) return;
        if (resolved) {
          setUrl(resolved);
          setState('ready');
        } else {
          setState('fallback');
        }
      })
      .catch(() => {
        if (!cancelled) setState('fallback');
      });

    return () => {
      cancelled = true;
    };
  }, [path]);

  return (
    <div
      className={`flex items-center justify-center overflow-hidden bg-gray-2 text-primary/40 dark:bg-meta-4 ${className}`}
    >
      {state === 'ready' && url ? (
        <img
          src={url}
          alt={alt}
          loading="lazy"
          className="h-full w-full object-cover"
          onError={() => setState('fallback')}
        />
      ) : (
        <Utensils
          className={`h-9 w-9 ${state === 'loading' ? 'animate-pulse' : ''}`}
        />
      )}
    </div>
  );
};

export default RestaurantMenuItemImage;
